import { useCallback, useEffect, useMemo, useState } from 'react';
import { PlaceReviewRepository } from 'repository/placeReviewRepository';
import { unwrapList, unwrapData } from 'common/utils/apiResponse';
import type { StorePhoto, StoreSlot } from 'components/place/StorePhotoSlots';
import type { VisitPhoto } from 'components/place/VisitPhotos';

/** 서버가 주는 후기 한 건 */
interface ApiReview {
    reviewId: number;
    userNo: number;
    nickname?: string | null;
    rating?: number | null;
    content?: string | null;
    tags?: string | null;
    likeCount?: number | null;
    liked?: boolean | null;
    mine?: boolean | null;
    createdAt?: string | null;
    photos?: { photoId: number; imageUrl: string }[] | null;
}

interface ApiPhoto {
    photoId: number;
    imageUrl: string;
    storeSlot?: StoreSlot | null;
    likeCount?: number | null;
}

/** 서버는 태그를 쉼표로 이어 붙인 한 줄로 저장합니다 */
const splitTags = (tags?: string | null): string[] =>
    (tags || '').split(',').map(t => t.trim()).filter(Boolean);

export const joinTags = (tags: string[]): string => tags.join(',');

const toVisitPhoto = (r: ApiReview): VisitPhoto => ({
    postId: r.reviewId,
    userNo: r.userNo,
    nickname: r.nickname || '',
    rating: r.rating || undefined,
    content: r.content,
    tags: splitTags(r.tags),
    photos: (r.photos || []).map(p => p.imageUrl).filter(Boolean),
    createdAt: r.createdAt || '',
    helpfulCount: r.likeCount || 0,
    helpful: !!r.liked,
    mine: !!r.mine,
});

const PAGE_SIZE = 20;

/**
 * 장소 한 곳의 사진과 후기.
 *
 * 모임 상세(일부만)와 장소 사진 화면(전체)이 같은 것을 봅니다.
 * 두 화면이 각자 부르면 형태가 갈라지므로 여기 한 곳에 둡니다.
 */
export const usePlaceReviews = (placeId?: string | null) => {
    const [storePhotos, setStorePhotos] = useState<StorePhoto[]>([]);
    const [posts, setPosts] = useState<VisitPhoto[]>([]);
    const [rating, setRating] = useState<number | null>(null);
    const [reviewCount, setReviewCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    const load = useCallback(async (nextPage = 1) => {
        if (!placeId) return;
        setLoading(true);
        try {
            const [storeRes, reviewRes, statsRes] = await Promise.all([
                PlaceReviewRepository.getStorePhotos(placeId),
                PlaceReviewRepository.getReviews(placeId, nextPage, PAGE_SIZE),
                // 통계는 첫 장에서만 — 더 불러올 때마다 다시 셀 이유가 없습니다
                nextPage === 1 ? PlaceReviewRepository.getStats(placeId) : Promise.resolve(null),
            ]);

            setStorePhotos(
                unwrapList<ApiPhoto>(storeRes)
                    .filter(p => !!p.storeSlot)
                    .map(p => ({
                        photoId: p.photoId,
                        slot: p.storeSlot as StoreSlot,
                        imageUrl: p.imageUrl,
                        likeCount: p.likeCount || 0,
                    })),
            );

            const reviews = unwrapList<ApiReview>(reviewRes).map(toVisitPhoto);
            setPosts(prev => (nextPage === 1 ? reviews : [...prev, ...reviews]));
            setHasMore(reviews.length === PAGE_SIZE);
            setPage(nextPage);

            if (statsRes) {
                const stats = unwrapData<{ avgRating?: number; reviewCount?: number }>(statsRes);
                setRating(stats?.avgRating ?? null);
                setReviewCount(stats?.reviewCount ?? 0);
            }
        } catch {
            // 사진과 후기는 곁다리입니다 — 못 불러와도 화면 전체를 막지 않습니다
        } finally {
            setLoading(false);
        }
    }, [placeId]);

    useEffect(() => {
        setPosts([]); setStorePhotos([]); setRating(null); setReviewCount(0); setPage(1);
        load(1);
    }, [load]);

    const loadMore = useCallback(() => {
        if (!loading && hasMore) load(page + 1);
    }, [loading, hasMore, page, load]);

    /** 「도움 됐어요」 — 서버 응답을 기다리지 않고 먼저 바꿔 보여줍니다 */
    const toggleHelpful = useCallback(async (post: VisitPhoto) => {
        const next = !post.helpful;
        setPosts(v => v.map(p => p.postId === post.postId
            ? { ...p, helpful: next, helpfulCount: Math.max(0, (p.helpfulCount || 0) + (next ? 1 : -1)) }
            : p));
        try {
            await PlaceReviewRepository.toggleLike(post.postId);
        } catch {
            // 실패하면 되돌립니다
            setPosts(v => v.map(p => p.postId === post.postId
                ? { ...p, helpful: post.helpful, helpfulCount: post.helpfulCount }
                : p));
        }
    }, []);

    const reload = useCallback(() => load(1), [load]);

    /* 돌려주는 객체를 고정합니다.
       매번 새로 만들면 이걸 의존성으로 잡은 useMemo·useCallback 이
       매 렌더 다시 돕니다 — 메모를 걸어놓고 안 걸린 상태가 됩니다. */
    return useMemo(() => ({
        storePhotos, posts, rating, reviewCount, loading, hasMore,
        reload, loadMore, toggleHelpful,
    }), [storePhotos, posts, rating, reviewCount, loading, hasMore, reload, loadMore, toggleHelpful]);
};

/** usePlaceReviews 가 돌려주는 것. 한 번 받아 여러 자리에 나눠 쓸 때 씁니다. */
export type PlaceFeed = ReturnType<typeof usePlaceReviews>;

/** 장소 사진·후기 화면으로 가는 경로.
 *
 *  모임 상세(미리보기 안 [전체보기]) 와 여행 일정의 장소 줄이 같은 화면을 엽니다.
 *  쿼리를 양쪽에 적어 두면 한쪽에만 name 을 빼먹는 식으로 어긋납니다. */
export const placePhotosPath = (
    placeId: string,
    opts?: { placeName?: string; meetingId?: string; canAdd?: boolean },
): string => {
    const q = new URLSearchParams({ name: opts?.placeName || '' });
    if (opts?.meetingId) q.set('meetingId', opts.meetingId);
    if (opts?.canAdd) q.set('canAdd', '1');
    return `/place/${encodeURIComponent(placeId)}/photos?${q.toString()}`;
};

/** 제목 배경으로 쓸 사진 — 가게 사진 먼저, 그 다음 방문 사진.
 *
 *  모임 상세·완료·여행 세 화면이 같은 순서로 같은 사진을 씁니다.
 *  세 곳에 각각 적어 두면 한 곳만 고치는 일이 생기기 마련입니다. */
export const feedPhotos = (feed?: PlaceFeed | null): string[] => {
    if (!feed) return [];
    return Array.from(new Set([
        ...feed.storePhotos.map(p => p.imageUrl),
        ...feed.posts.flatMap(p => p.photos),
    ].filter(Boolean)));
};
